import { useTranslation } from 'react-i18next';
import { Icon } from '@sdb/planning-ui';

import { EntitySearchListItem } from 'components/mrp/components/EntitySearch/EntitySearch.styles';

import { OrganizationalUnitIcon } from '../../PlanningEntityPicker.styles';
import { getOrganizationalUnitIconType } from '../../PlanningEntityPicker.utils';
import {
  OptionName,
  OptionPerspective,
  OptionRow,
  OptionText,
  PerspectiveSegment,
  PerspectiveSwitch
} from './OrganizationalUnitOptionItem.styles';

import type { MouseEvent } from 'react';
import type { OrganizationalUnitPerspective } from '../../PlanningEntityPicker.types';
import type { OrganizationalUnitOptionItemProps } from './OrganizationalUnitOptionItem.types';

/**
 * A plan unit or plan group on one row: icon, name, "Planeenheid"/"Plangroep", and a switch between the
 * perspectives it still offers. A click on the row picks the one the switch stands at; a click on a
 * segment picks that one.
 */
const OrganizationalUnitOptionItem = ({
  props,
  selection,
  perspectives,
  activePerspective,
  onPickPerspective
}: OrganizationalUnitOptionItemProps) => {
  'use memo';

  const { t } = useTranslation('common');
  const isGroup = typeof selection.organizationalUnitId === 'string';
  const getPerspectiveLabel = (perspective: OrganizationalUnitPerspective) =>
    t(`planningEntityPicker.perspectives.${perspective}`);

  // Focus stays in the search, as the list's own rows leave it.
  const handleSegmentMouseDown = (event: MouseEvent) => event.preventDefault();

  const handleSegmentClick = (
    event: MouseEvent,
    perspective: OrganizationalUnitPerspective
  ) => {
    // The segment's pick, not the row's.
    event.stopPropagation();
    onPickPerspective(perspective);
  };

  return (
    <EntitySearchListItem
      {...props}
      // The name a screen reader announces says what Enter picks now; the switch itself is for the mouse.
      aria-label={`${selection.label}, ${getPerspectiveLabel(
        activePerspective
      )}`}
    >
      <OptionRow>
        <OrganizationalUnitIcon>
          <Icon
            type={getOrganizationalUnitIconType(selection.organizationalUnitId)}
            fontSize="small"
          />
        </OrganizationalUnitIcon>
        <OptionText>
          <OptionName>{selection.label}</OptionName>
          <OptionPerspective>
            {isGroup ? t('planGroup') : t('planUnit')}
          </OptionPerspective>
        </OptionText>
        <PerspectiveSwitch aria-hidden>
          {perspectives.map((perspective) => (
            <PerspectiveSegment
              key={perspective}
              active={perspective === activePerspective}
              onMouseDown={handleSegmentMouseDown}
              onClick={(event) => handleSegmentClick(event, perspective)}
            >
              {getPerspectiveLabel(perspective)}
            </PerspectiveSegment>
          ))}
        </PerspectiveSwitch>
      </OptionRow>
    </EntitySearchListItem>
  );
};

export default OrganizationalUnitOptionItem;
